import React from 'react';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import clsx from 'clsx';
import { Event, eventGroupLabel } from '../../types/event';
import { getEventsForDay, getTimeSlots, getEventPosition, formatEventTime, getEventColor, isAllDayEvent } from '../../utils/calendar';

interface DayViewProps {
  currentDate: Date;
  events: Event[];
  onEventClick: (event: Event) => void;
  onTimeSlotClick: (date: Date, time?: string) => void;
  canModifyEvents: boolean;
}

const DayView: React.FC<DayViewProps> = ({
  currentDate,
  events,
  onEventClick,
  onTimeSlotClick,
  canModifyEvents,
}) => {
  const dayEvents = getEventsForDay(currentDate, events);
  const timeSlots = getTimeSlots();

  const handleTimeSlotClick = (timeSlot: string) => {
    if (!canModifyEvents) return;

    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotDate = new Date(currentDate);
    slotDate.setHours(hours, minutes, 0, 0);
    onTimeSlotClick(slotDate, timeSlot);
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="text-2xl font-bold text-gray-900">
            {format(currentDate, 'd')}
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {format(currentDate, 'EEEE', { locale: hu })}
            </div>
            <div className="text-sm text-gray-600">
              {format(currentDate, 'yyyy MMMM', { locale: hu })}
            </div>
          </div>
          {dayEvents.length > 0 && (
            <div className="text-sm text-gray-600">
              {dayEvents.length} esemény
            </div>
          )}
        </div>
      </div>

      {/* All Day Events */}
      {dayEvents.some(event => event.isAllDay) && (
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="p-3">
            <div className="text-xs font-medium text-gray-700 mb-2">Egész napos</div>
            <div className="space-y-1">
              {dayEvents
                .filter(event => isAllDayEvent(event))
                .map((event) => (
                  <div
                    key={event.id}
                    onClick={() => onEventClick(event)}
                    className={clsx(
                      'p-2 rounded cursor-pointer hover:opacity-80 border transition-opacity',
                      getEventColor(event.type)
                    )}
                  >
                    <div className="font-medium">
                      {event.title}
                      {eventGroupLabel(event) && <span className="opacity-75 font-normal"> ({eventGroupLabel(event)})</span>}
                    </div>
                    {event.location && (
                      <div className="text-sm opacity-75 mt-1">
                        📍 {event.location}
                      </div>
                    )}
                    {event.description && (
                      <div className="text-sm opacity-75 mt-1">
                        {event.description}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Time Grid */}
      <div className="flex-1 overflow-auto">
        <div className="flex">
          {/* Time Column */}
          <div className="w-20 flex-shrink-0 border-r border-gray-200">
            {timeSlots.map((timeSlot) => (
              <div
                key={timeSlot}
                className="h-15 border-b border-gray-200 text-sm text-gray-600 p-2 text-right font-medium"
                style={{ height: '60px' }}
              >
                {timeSlot}
              </div>
            ))}
          </div>

          {/* Events Column */}
          <div className="flex-1 relative">
            {/* Time Slots */}
            {timeSlots.map((timeSlot) => (
              <div
                key={timeSlot}
                className="h-15 border-b border-gray-100 cursor-pointer hover:bg-gray-50 relative"
                style={{ height: '60px' }}
                onClick={() => handleTimeSlotClick(timeSlot)}
              >
                {/* Half-hour line */}
                <div className="absolute top-1/2 left-0 right-0 border-t border-gray-100"></div>
              </div>
            ))}

            {/* Events */}
            {dayEvents
              .filter(event => !isAllDayEvent(event))
              .map((event) => {
                const position = getEventPosition(event, currentDate);
                return (
                  <div
                    key={event.id}
                    className={clsx(
                      'absolute left-2 right-2 rounded p-2 cursor-pointer hover:opacity-80 z-10 border shadow-sm transition-opacity',
                      getEventColor(event.type)
                    )}
                    style={{
                      top: `${position.top}px`,
                      height: `${Math.max(position.height, 30)}px`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                  >
                    <div className="font-medium text-sm">
                      {event.title}
                      {eventGroupLabel(event) && <span className="opacity-75 font-normal"> ({eventGroupLabel(event)})</span>}
                    </div>
                    <div className="text-xs opacity-75 mt-1">
                      {formatEventTime(event)}
                      {event.location && <span> · 📍 {event.location}</span>}
                    </div>
                    {event.description && position.height > 70 && (
                      <div className="text-xs opacity-75 mt-2 line-clamp-2">
                        {event.description}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Summary */}
      {dayEvents.length === 0 && (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <div className="text-lg font-medium mb-2">Nincsenek események</div>
            <div className="text-sm">
              {canModifyEvents
                ? 'Kattints egy időpontra új esemény létrehozásához'
                : 'Ezen a napon nincs esemény ütemezve'
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayView;